import { CardElementContainer, Input, StripeError } from "@components/primitives/input";
import { ButtonWrap } from "@components/value-props/ValueProps.styled";
import { AddressElement, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Address, BillingDetails } from "@stripe/stripe-js";
import { invalidField } from "@utils/validations";
import { Form, Formik } from "formik";
import { Dispatch, SetStateAction, useState } from "react";
import * as Yup from "yup";
import { ButtonContainer, FormSection } from "./CustomerInfo.styled";

interface Props {
  handleSubmit: ({ paymentId, postalCode }: { paymentId: string; postalCode: string }) => void;
  handleError: Dispatch<SetStateAction<string>>;
  intentToken?: string;
}

export const cardFormInitialValues = { name: "" };

const infoSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(1, invalidField("Name", 1))
    .max(50, invalidField("Name", 1))
    .required("Name Required"),
});

const InfoForm = ({ handleSubmit, handleError, intentToken }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [addressInfo, setAddressInfo] = useState<BillingDetails>();

  const createPaymentMethod = async (name: string, address: Address) => {
    const { paymentMethod, error: paymentError } = await stripe.createPaymentMethod({
      card: elements.getElement(CardElement),
      type: "card",
      billing_details: { name, address },
    });

    if (paymentError) {
      return { stripeError: true, error: "Missing values on the card form" };
    }

    // Confirm card setup with fetched secret token
    try {
      const { setupIntent } = await stripe.confirmCardSetup(intentToken, {
        payment_method: paymentMethod.id,
      });

      return setupIntent.status === "succeeded"
        ? { paymentId: paymentMethod.id, postalCode: address.postal_code }
        : { stripeError: true, error: "Missing values on the card form" };
    } catch (error) {
      return { stripeError: true, error: "Confirm Card Setup error" };
    }
  };

  const submitPayment = async (values) => {
    const { paymentId, postalCode } = await createPaymentMethod(values.name, addressInfo.address);
    handleSubmit({ paymentId, postalCode });
  };

  return (
    <>
      <Formik
        initialValues={{
          ...cardFormInitialValues,
        }}
        validationSchema={infoSchema}
        onSubmit={submitPayment}>
        {(props) => {
          const { values, errors, touched, handleChange, handleBlur, isSubmitting } = props;
          return (
            <Form noValidate>
              <FormSection>
                <h4>Credit Card</h4>
                <Input
                  type="text"
                  name="name"
                  label="Cardholder Name"
                  autoComplete="cc-name"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  error={(errors.name as string) && touched.name && (errors.name as string)}
                />
                {stripe ? (
                  <CardElementContainer>
                    <CardElement />
                  </CardElementContainer>
                ) : (
                  <StripeError />
                )}
              </FormSection>
              <FormSection>
                <h4>Shipping Address</h4>
                {stripe ? (
                  <CardElementContainer>
                    <AddressElement
                      options={{ mode: "billing" }}
                      onChange={(event) => {
                        setAddressInfo(event.value);
                      }}
                    />
                  </CardElementContainer>
                ) : (
                  <StripeError />
                )}
              </FormSection>
              <ButtonContainer>
                <ButtonWrap>
                  <input
                    type="submit"
                    className="button inverted"
                    value="Complete Transfer"
                    disabled={isSubmitting}
                  />
                </ButtonWrap>
              </ButtonContainer>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default InfoForm;
